import { useCallback, useEffect, useRef, useState } from 'react';

/** Default duration in ms before the "new" highlight is removed */
const DEFAULT_HIGHLIGHT_DURATION = 3000;

interface IHighlightedItem {
  id: string;
}

interface IWithHighlight {
  /** Whether this item is newly added (for animation/highlight) */
  isNew: boolean;
}

interface IUseHighlightedListOptions {
  /** Duration in ms before the highlight is removed. Defaults to 3000ms. */
  duration?: number;
}

interface IUseHighlightedListReturn<T extends IHighlightedItem> {
  /** The list of items with isNew flag */
  items: Array<T & IWithHighlight>;
  /** Add a new item to the beginning of the list with highlight */
  addItem: (item: T) => void;
  /** Clear all items from the list */
  clearItems: () => void;
}

/**
 * Hook for managing a list of items with temporary "new" highlights.
 * Items are prepended to the list and their isNew flag is automatically
 * removed after the specified duration.
 *
 * @param options - Configuration options
 * @returns Object with items array and addItem function
 *
 * @example
 * ```tsx
 * interface ITransaction {
 *   id: string;
 *   amount: number;
 * }
 *
 * function TransactionList() {
 *   const { items, addItem } = useHighlightedList<ITransaction>({ duration: 3000 });
 *
 *   return (
 *     <ul>
 *       {items.map(tx => (
 *         <li key={tx.id} className={tx.isNew ? 'highlight' : ''}>
 *           ${tx.amount}
 *         </li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
function useHighlightedList<T extends IHighlightedItem>(
  options: IUseHighlightedListOptions = {},
): IUseHighlightedListReturn<T> {
  const { duration = DEFAULT_HIGHLIGHT_DURATION } = options;
  const [items, setItems] = useState<Array<T & IWithHighlight>>([]);
  const timeoutIdsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup all timeouts on unmount
  useEffect(() => {
    const timeoutIds = timeoutIdsRef.current;
    return (): void => {
      timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
      timeoutIds.clear();
    };
  }, []);

  const removeHighlight = useCallback((itemId: string): void => {
    setItems((prev) =>
      prev.map((existingItem) =>
        existingItem.id === itemId ? { ...existingItem, isNew: false } : existingItem,
      ),
    );
    timeoutIdsRef.current.delete(itemId);
  }, []);

  const addItem = useCallback(
    (item: T): void => {
      const newItem: T & IWithHighlight = {
        ...item,
        isNew: true,
      };

      setItems((prev) => [newItem, ...prev]);

      const timeoutId = setTimeout(() => removeHighlight(item.id), duration);
      timeoutIdsRef.current.set(item.id, timeoutId);
    },
    [duration, removeHighlight],
  );

  const clearItems = useCallback((): void => {
    // Clear all pending timeouts
    timeoutIdsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    timeoutIdsRef.current.clear();
    setItems([]);
  }, []);

  return {
    items,
    addItem,
    clearItems,
  };
}

export default useHighlightedList;
export type {
  IHighlightedItem,
  IWithHighlight,
  IUseHighlightedListOptions,
  IUseHighlightedListReturn,
};
